/*
 * Project: bellcloud
 * File: packages/ui/components/common/device/DeviceInfoCard.tsx
 * Author: SWAN
 * Created: 2025-01-XX
 * Description: 디바이스 정보 표시 공통 컴포넌트 (지도 InfoWindow 등)
 */

import React from 'react';
import type { DeviceInfoCardData } from './types';
import { DeviceStatusCard, type SensorStatusData } from './DeviceStatusCard';
import { mapSensorStatus } from '@/utils/monitoring/sensorStatusMapper';

// ==================== Main Component ====================
interface DeviceInfoCardProps {
  data: DeviceInfoCardData;
}

export const DeviceInfoCard: React.FC<DeviceInfoCardProps> = ({ data }) => {
  const {
    name,
    serialNumber,
    deviceType,
    timestamp,
    createdAt,
    temperature,
    batteryStateValue,
    deviceImageUrl,
    sensors,
  } = data;

  const mappedSensors: SensorStatusData[] = (sensors || []).map(s => {
    // status_name 배열에서 첫 번째 값 사용 (우선순위 최고)
    const primaryStatus = s.status[0] || 'Normal';
    
    const statusLevel = mapSensorStatus(s.status, s.warning, s.error);

    // Tag 표시: status_name의 첫 번째 값 (Normal이 아니면 표시)
    const statusLabel =
      statusLevel !== 'Stable' && primaryStatus !== 'Normal'
        ? primaryStatus  // 첫 번째 status_name 값 (Warning, STEL Alarm 등)
        : undefined;

    // 부가정보: error 또는 warning (status_name과 다른 경우)
    const additionalInfo = s.error || (statusLevel === 'Warning' && s.warning !== primaryStatus ? s.warning : undefined) || undefined;

    return {
      type: s.type,
      value: s.concentration,
      unit: '',
      status: statusLevel,
      statusLabel,
      additionalInfo,
    };
  });

  return (
    <DeviceStatusCard
      deviceName={name || serialNumber || ''}
      serialNumber={serialNumber || ''}
      deviceType={deviceType || ''}
      timestamp={timestamp || createdAt || ''}
      temperature={temperature ?? undefined}
      batteryState={batteryStateValue ?? undefined}
      deviceImageUrl={deviceImageUrl}
      sensors={mappedSensors}
    />
  );
};
